import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import ReactTable from 'react-table';
import AddIcon from '@mui/icons-material/Add';
import { FormControlLabel } from '@mui/material';
import { Button as MuiButton } from '@mui/material';

import { PageInfoHeader } from 'components/PageInfoHeader/PageInfoHeader';
import AddOrEditPersonnelModal from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/AddOrEditPersonnelModal';
import SearchPersonnelModal from 'components/pages/study/StudyDetails/SiteDetails/SearchPersonnelModal/SearchPersonnelModal';
import SsuOnHoldCheckbox from 'components/pages/study/StudyDetails/SiteDetails/SsuOnHoldCheckbox/SsuOnHoldCheckbox';
import { withParams } from 'components/root/router/legacyComponentCompatability';

import { StudyApi, StudySiteApi } from '../../../../../api';
import common from '../../../../../common/common';
import Checkbox from '../../../../../common/data-entry/InputSelectors/Checkbox';
import ModalBoxes from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import { STDT_REIMBURSEMENT, STTG_CTMS } from '../../../../../constants/study/studyTypes';
import {
  EDIT_PRIMARY_STUDY_MANAGER_AND_INVESTIGATOR,
  MANAGE_PATIENT_STIPEND_FIELDS_REQUIRED,
  MANAGE_SSU_REIMBURSEMENT_EMAIL_NOTIFICATION,
  MANAGE_SSU_STIPEND_OVERSIGHT,
  USER_SELF_SERVICE_ACCESS
} from '../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../services/auth';
import { formatPhoneNumber } from '../../../../../services/geo/e-164';
import { onRequestDefaultError } from '../../../../../services/handlers';
import { NS_COMMON, NSC_ADDRESS, NSC_STUDY, NSPS_SITE } from '../../../../../services/i18n/namespaces';
import { RT_TEXT_OVERWRITE } from '../../../../../services/i18n/react-table';
import LeadModal from '../LeadModal/LeadModal';

import ExpectedSuccessMetricsTable from './ExpectedSuccessMetrics/ExpectedSuccessMetricsTable';
import InformedConsentTable from './InformedConsentTable';

import 'react-table/react-table.css';
import 'react-datetime/css/react-datetime.css';
import './SiteDetails.scss';

class SiteDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      siteDetails: {
        onHold: false
      },
      personnelDetailsColumns: [],
      primarySm: null,
      selectedSm: null,
      primarySmName: null,
      primaryInvestigator: null,
      selectedInvestigator: null,
      primaryInvestigatorName: null,
      isStudyTypeCTMS: false,
      isStudyReimbursementOption: false
    };
  }

  getColumns() {
    const { t } = this.props;
    const generalColumns = [
      {
        Header: t('personnelDetails.role', { ns: NSPS_SITE }),
        accessor: 'role',
        Cell: row => {
          return row.row.role ? <span> {row.row.role} </span> : '--';
        }
      },
      {
        id: 'group',
        Header: t('personnelDetails.group', { ns: NSPS_SITE }),
        accessor: 'groupAssign',
        sortable: true,
        Cell: row => {
          return row.row.group ? <span> {row.row.group.name + ' (' + row.row.group.type + ')'} </span> : null;
        }
      },
      {
        id: 'Name',
        Header: t('name'),
        accessor: 'name',
        sortable: true,
        Cell: row => {
          return row.row._original ? <span> {row.row._original.name} </span> : '--';
        }
      },
      {
        Header: t('email'),
        accessor: 'email',
        sortable: true,
        Cell: row => {
          return row.row._original ? <span> {row.row._original.email} </span> : '--';
        }
      },
      {
        Header: t('startDate', { ns: NS_COMMON }),
        accessor: 'start_date',
        sortable: true,
        Cell: row => {
          return row.row.start_date ? <span> {row.row.start_date} </span> : '--';
        },
        sortMethod: common.dateComparator
      },
      {
        Header: t('endDate', { ns: NS_COMMON }),
        accessor: 'end_date',
        Cell: row => {
          return row.row.end_date ? <span> {row.row.end_date} </span> : '--';
        },
        sortMethod: common.dateComparator
      },
      {
        Header: t('status'),
        accessor: 'status',
        Cell: row => {
          return row.row.status ? <span> {row.row.status} </span> : '--';
        }
      }
    ];
    const restrictedColumns = [
      {
        Header: t('personnelDetails.actions', { ns: NSPS_SITE }),
        Cell: row => {
          return (
            <MuiButton
              variant="text"
              sx={{ textTransform: 'none' }}
              onClick={() => this.openEditPersonnelModal(row.row._original.personnelIdentifier)}
            >
              {t('edit', { ns: NS_COMMON })}
            </MuiButton>
          );
        }
      }
    ];
    return userHasAccessTo(USER_SELF_SERVICE_ACCESS) ? [...generalColumns, ...restrictedColumns] : generalColumns;
  }

  componentDidMount() {
    localStorage.setItem('fromMenu', false);
    let personnelDetailsColumns = this.getColumns();
    try {
      const data = this.props.params.siteName;
      const studyId = this.props.params.studyName;
      StudyApi.getSiteDetails(data).then(({ data: { response } }) => {
        const primarySm = response.primaryStudyManager ? response.primaryStudyManager : null;
        const primarySmName = primarySm ? primarySm.name : null;
        const primaryInvestigator = response.primaryInvestigator ? response.primaryInvestigator : null;
        const primaryInvestigatorName = primaryInvestigator ? primaryInvestigator.name : null;
        personnelDetailsColumns = this.getColumns();
        this.setState({
          siteDetails: response,
          selectedSm: primarySm,
          selectedInvestigator: primaryInvestigator,
          primarySm,
          primarySmName,
          primaryInvestigator,
          primaryInvestigatorName,
          personnelDetailsColumns
        });
      }, onRequestDefaultError);
      StudyApi.getStudyTypes(studyId).then(res => {
        const isStudyTypeCTMS = res.data.type === STTG_CTMS;
        const isStudyReimbursementOption = res.data.typeParents
          .find(e => e.typeParent === STTG_CTMS)
          .types.find(e => e.name === STDT_REIMBURSEMENT)?.selected;
        this.setState({
          isStudyTypeCTMS,
          isStudyReimbursementOption
        });
      }, onRequestDefaultError);
    } catch (e) {}
  }

  prepareLeadList = (authorityIdentifier, currentLead) => {
    const preparedList = this.state.siteDetails.sitePersonnels
      .filter(it => it.authority_identifier === authorityIdentifier)
      .sort((a, b) => (a.name > b.name ? 1 : -1));
    if (currentLead) {
      const unassignedSelect = {
        name: 'Unassigned'
      };
      preparedList.unshift(unassignedSelect);
    }
    return preparedList;
  };

  openLeadInvestigatorModal = () => {
    const piList = this.prepareLeadList('DS-AUTH-000002', this.state.primaryInvestigator);
    ModalBoxes.open({
      title: this.props.t('leadInvestigator', { ns: NSPS_SITE }),
      component: (
        <LeadModal
          leadLabel={this.props.t('investigator', { ns: NSPS_SITE })}
          leadList={piList}
          currentLead={this.state.primaryInvestigator}
          handleLeadChange={this.handleLeadInvestigatorChange}
          saveLead={this.saveLeadInvestigator}
        />
      )
    });
  };

  openLeadSmModal = () => {
    const smList = this.prepareLeadList('DS-AUTH-000001', this.state.primarySm);
    ModalBoxes.open({
      title: this.props.t('leadStudyManager', { ns: NSPS_SITE }),
      component: (
        <LeadModal
          leadLabel={this.props.t('studyManager', { ns: NSPS_SITE })}
          leadList={smList}
          currentLead={this.state.primarySm}
          handleLeadChange={this.handleLeadSmChange}
          saveLead={this.saveLeadSm}
        />
      )
    });
  };

  handleLeadInvestigatorChange = pi => {
    this.setState({ selectedInvestigator: pi });
  };

  handleLeadSmChange = sm => {
    this.setState({ selectedSm: sm });
  };

  saveLeadInvestigator = () => {
    const piId = this.state.selectedInvestigator ? this.state.selectedInvestigator.personnelIdentifier : null;
    const pi = { ssuId: this.state.siteDetails.unique_identifier, personnelId: piId };

    StudySiteApi.setPrimaryInvestigator(pi).then(resp => {
      const siteId = this.props.params.siteName;
      StudyApi.getSiteDetails(siteId).then(({ data: { response } }) => {
        const primaryInvestigator = response.primaryInvestigator ? response.primaryInvestigator : null;
        const primaryInvestigatorName = primaryInvestigator ? primaryInvestigator.name : null;
        this.setState({
          siteDetails: response,
          primaryInvestigator,
          primaryInvestigatorName
        });
      }, onRequestDefaultError);
    });
  };

  saveLeadSm = () => {
    const studyManagerId = this.state.selectedSm ? this.state.selectedSm.personnelIdentifier : null;
    const primaryStudyManager = { ssuId: this.state.siteDetails.unique_identifier, personnelId: studyManagerId };

    StudySiteApi.setPrimarySM(primaryStudyManager).then(resp => {
      const siteId = this.props.params.siteName;
      StudyApi.getSiteDetails(siteId).then(({ data: { response } }) => {
        const primarySm = response.primaryStudyManager ? response.primaryStudyManager : null;
        const primarySmName = primarySm ? primarySm.name : null;
        this.setState({
          siteDetails: response,
          primarySm,
          primarySmName
        });
      }, onRequestDefaultError);
    });
  };

  updateReimbursementEmailOptIn = () => {
    const emailOptIn = !this.state.siteDetails?.reimbursementEmailOptIn;
    StudySiteApi.updateReimbursementEmailOptIn(this.props.params.siteName, emailOptIn).then(({ data }) => {
      this.setState(state => {
        return { siteDetails: { ...state.siteDetails, reimbursementEmailOptIn: data } };
      });
    });
  };

  updateStipendOversightOption = () => {
    const stipendOversight = !this.state.siteDetails?.stipendOversight;
    StudySiteApi.updateStipendOversightOption(this.props.params.siteName, stipendOversight).then(({ data }) => {
      this.setState(state => {
        return { siteDetails: { ...state.siteDetails, stipendOversight: data } };
      });
    });
  };

  updatePatientStipendFieldsRequiredOption = () => {
    const patientStipendFieldsRequired = !this.state.siteDetails?.patientStipendFieldsRequired;
    StudySiteApi.updatePatientStipendFieldsRequiredOption(
      this.props.params.siteName,
      patientStipendFieldsRequired
    ).then(({ data }) => {
      this.setState(state => {
        return { siteDetails: { ...state.siteDetails, patientStipendFieldsRequired: data } };
      });
    });
  };

  openSearchPersonnelModal = () => {
    ModalBoxes.open({
      component: (
        <SearchPersonnelModal
          ssuId={this.props.params?.siteName}
          studyId={this.props.params.studyName}
          studyName={this.state.siteDetails.study_name}
          isGroupAssignStudy={this.state.siteDetails.groupAssignStudy}
          siteName={this.state.siteDetails.name}
        />
      ),
      title: this.props.t('searchPersonnel.title', { ns: NSPS_SITE })
    });
  };

  openEditPersonnelModal = personnelId => {
    ModalBoxes.open({
      component: (
        <AddOrEditPersonnelModal
          ssuId={this.props.params?.siteName}
          studyId={this.props.params.studyName}
          studyName={this.state.siteDetails.study_name}
          isGroupAssignStudy={this.state.siteDetails.groupAssignStudy}
          siteName={this.state.siteDetails.name}
          personnelId={personnelId}
        />
      ),
      title: this.props.t('editPersonnel', { ns: NSPS_SITE })
    });
  };

  formatPhoneNumber = (phone, phoneCode) => {
    if (!phone) return '';
    if (!phoneCode) return phone;
    return formatPhoneNumber(phone, phoneCode);
  };

  render({ props, state } = this) {
    const { siteDetails } = state;
    const { isStudyTypeCTMS } = state;
    const { isStudyReimbursementOption } = state;
    const { studyName } = props.params;
    const { t } = props;
    return (
      <>
        <PageInfoHeader
          objectRecordLabel={siteDetails.name}
          pageInfo={
            <PageInfoHeader.AdditionalInfo tooltip="Study">{siteDetails.study_name}</PageInfoHeader.AdditionalInfo>
          }
        />
        <section className="siteDetails footpadb iefixflex">
          <div className="p-3 m-0 border row justify-content-between">
            <div className="row">
              <div className="col-md-2">
                <div className="form-group">
                  <label htmlFor="site">{t('name')}</label>
                  <input className="form-control" value={siteDetails.name || ''} disabled />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('status')}</label>
                  <input className="form-control" value={siteDetails.status || ''} disabled />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('preStudyVisit', { ns: NSC_STUDY })}</label>
                  <input className="form-control" disabled value={siteDetails.psvDate || ''} />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('siteInitiationVisit', { ns: NSPS_SITE })}</label>
                  <input className="form-control" disabled value={siteDetails.sivDate || ''} />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('sponsorGreenLight', { ns: NSPS_SITE })}</label>
                  <input className="form-control" disabled value={siteDetails.grnDate || ''} />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('award', { ns: NSPS_SITE })}</label>
                  <input className="form-control" disabled value={siteDetails.awardDate || ''} />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('contractExecuted', { ns: NSPS_SITE })}</label>
                  <input className="form-control" disabled value={siteDetails.contractExecutedDate || ''} />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('leadInvestigator', { ns: NSC_STUDY })}</label>
                  <div className="input-group">
                    <span className="col-9 mr-0 p-0">
                      <input
                        type="text"
                        className="form-control"
                        value={this.state.primaryInvestigatorName ? this.state.primaryInvestigatorName : ''}
                        disabled
                      />
                    </span>
                    <span className="col-3 pt-1 pr-0">
                      {userHasAccessTo(EDIT_PRIMARY_STUDY_MANAGER_AND_INVESTIGATOR) ? (
                        <i className="glyphicon glyphicon-edit pr-2 editicn" onClick={this.openLeadInvestigatorModal} />
                      ) : (
                        ''
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('leadStudyManager', { ns: NSPS_SITE })}</label>
                  <div className="input-group">
                    <span className="col-9 mr-0 p-0">
                      <input
                        type="text"
                        className="form-control"
                        value={this.state.primarySmName ? this.state.primarySmName : ''}
                        disabled
                      />
                    </span>
                    <span className="col-3 pt-1 pr-0">
                      {userHasAccessTo(EDIT_PRIMARY_STUDY_MANAGER_AND_INVESTIGATOR) ? (
                        <i className="glyphicon glyphicon-edit pr-2 editicn" onClick={this.openLeadSmModal} />
                      ) : (
                        ''
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 col-md-12 col-lg-12" />
              <div className="col-md-4 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('addressLine1', { ns: NSPS_SITE })}</label>
                  <input type="text" className="form-control" value={siteDetails.address1 || ''} disabled />
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('state', { ns: NSC_ADDRESS })}</label>
                  <div className="input-group">
                    <input type="text" className="form-control" value={siteDetails.state || ''} disabled />
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('city', { ns: NSC_ADDRESS })}</label>
                  <div className="input-group">
                    <input type="text" className="form-control" value={siteDetails.city || ''} disabled />
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('zipCode', { ns: NSC_ADDRESS })}</label>
                  <div className="input-group">
                    <input type="text" className="form-control" value={siteDetails.zipCode || ''} disabled />
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-half-offset">
                <div className="form-group">
                  <label htmlFor="site">{t('phone', { ns: NS_COMMON })}</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={this.formatPhoneNumber(siteDetails.phone, siteDetails.phoneCode)}
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="col-xs-12 col-md-12 col-lg-12" />
              <div className="col-md-4 col-half-offset">
                <div className="form-group">
                  <label>{t('addressLine2', { ns: NSPS_SITE })}</label>
                  <input type="text" className="form-control" value={siteDetails.address2 || ''} disabled />
                </div>
              </div>
              <div className="col-2">
                <FormControlLabel
                  value="top"
                  control={
                    <SsuOnHoldCheckbox
                      ssuId={this.props.params.siteName}
                      onHold={siteDetails.onHold}
                      onChange={onHold =>
                        this.setState(state => {
                          return { siteDetails: { ...state.siteDetails, onHold } };
                        })
                      }
                    />
                  }
                  label={t('onHold', { ns: NSPS_SITE })}
                  labelPlacement="top"
                  sx={{ alignItems: 'start' }}
                  slotProps={{
                    typography: {
                      sx: { fontSize: '.9rem' }
                    }
                  }}
                />
              </div>
              <div className="col-2">
                <FormControlLabel
                  value="top"
                  control={
                    <Checkbox
                      checked={siteDetails?.patientStipendFieldsRequired}
                      disabled={!userHasAccessTo(MANAGE_PATIENT_STIPEND_FIELDS_REQUIRED)}
                      onChange={this.updatePatientStipendFieldsRequiredOption}
                    />
                  }
                  label={t('patientStipendFieldsRequired', { ns: NSPS_SITE })}
                  labelPlacement="top"
                  sx={{ alignItems: 'start' }}
                  slotProps={{
                    typography: {
                      sx: { fontSize: '.9rem' }
                    }
                  }}
                />
              </div>
              {isStudyTypeCTMS && isStudyReimbursementOption && (
                <div className="col-2">
                  <FormControlLabel
                    value="top"
                    control={
                      <Checkbox
                        checked={siteDetails?.reimbursementEmailOptIn}
                        disabled={!userHasAccessTo(MANAGE_SSU_REIMBURSEMENT_EMAIL_NOTIFICATION)}
                        onChange={this.updateReimbursementEmailOptIn}
                      />
                    }
                    label={t('reimbursementStatusEmailOptIn', { ns: NSPS_SITE })}
                    labelPlacement="top"
                    sx={{ alignItems: 'start' }}
                    slotProps={{
                      typography: {
                        sx: { fontSize: '.9rem' }
                      }
                    }}
                  />
                </div>
              )}
              {isStudyTypeCTMS && (
                <div className="col-2">
                  <FormControlLabel
                    value="top"
                    control={
                      <Checkbox
                        checked={siteDetails?.stipendOversight}
                        disabled={!userHasAccessTo(MANAGE_SSU_STIPEND_OVERSIGHT)}
                        onChange={this.updateStipendOversightOption}
                      />
                    }
                    label={t('stipendOversight', { ns: NSPS_SITE })}
                    labelPlacement="top"
                    sx={{ alignItems: 'start' }}
                    slotProps={{
                      typography: {
                        sx: { fontSize: '.9rem' }
                      }
                    }}
                  />
                </div>
              )}
              <div className="col-xs-12 col-md-12 col-lg-12" />
            </div>
            <InformedConsentTable ssuId={siteDetails.unique_identifier} studyId={studyName} />
            <div className="row col-12 px-0 pt-1 pb-3 m-0">
              <div className="col-md-12 row m-0 p-0 mb-2 justify-content-between border-bottom-dotted pb-0">
                <h5 className="mt-2 col-md-8 col-sm-auto p-0 c-p ">{t('personnelDetails.title', { ns: NSPS_SITE })}</h5>
                {userHasAccessTo(USER_SELF_SERVICE_ACCESS) && (
                  <span>
                    <MuiButton variant="text" sx={{ textTransform: 'none' }} onClick={this.openSearchPersonnelModal}>
                      <AddIcon />
                      {t('user', { ns: NSPS_SITE })}
                    </MuiButton>
                  </span>
                )}
              </div>
              <div className="col-12 border px-0 site-info-table">
                <ReactTable
                  {...RT_TEXT_OVERWRITE}
                  data={siteDetails.sitePersonnels}
                  columns={state.personnelDetailsColumns}
                  minRows={1}
                  multiSort
                  className="table activity-table table-responsive-sm m-0"
                  showPagination
                  nextText=">>"
                  previousText="<<"
                  defaultPageSize={10}
                  noDataText={t('noRecordFound')}
                  sortable
                />
              </div>
            </div>
            <ExpectedSuccessMetricsTable siteDetails={siteDetails} />
          </div>
        </section>
      </>
    );
  }
}

export default withTranslation()(withParams(SiteDetails));
